import { Action, Dispatch } from 'redux';
import moment from 'moment';
import _ from 'lodash';
import toast from '../../utils/toast';
import { IAppState } from '../reducers';
import {
  saveLiveAsset, restartLiveStream as restartLiveStreamApi, recoverLiveStream as recoverLiveStreamApi,
  updateAsset as updateAssetApi, getAsset,
} from '../../api/vault/assets';
import { formUtcStringFromDate } from '../../utils/time';
import { getChannelsByOrgId } from '../../api/vault/orgChannels';
import { KeyRoleUserVaultRoles, selectUserRole, selectUserOrgsSecurityProfilesIds } from '../user/userOrgRole';
import { getSecurityProfileOptions, GroupOptions } from '../securityProfiles';

import {
  ILookupOption, LookupName, selectExchangeGameTypeOptions, selectExchangeSportOptions,
  selectLeagueOptions, selectOptionsByName,
} from '../lookups';
import { AssetHit } from '../../api/vault/searchEngine';
import { getAllTeamsBySport, IVaultTeam } from '../../api/vault/teams';
import { League, Sport } from '../../enums';
import { createGame, getSchedules } from '../../api/vault/exchangeSchedule';

export interface ITeamOption {
  label: string;
  value: string;
  teamData: IVaultTeam;
}

export interface IState {
  isOpened: boolean,
  extendMode: boolean,
  restartMode: boolean,
  isButtonDisabled: boolean,
  isRTMPFeed: boolean,
  title: string,
  url: string,
  // Null when the event has no stored bitrate (auto mode)
  bitrate: number | null,
  // When true, streamBitrate is omitted so the stream host derives it
  bitrateAuto: boolean,
  // True once the event's stream host (EC2) has been created — output
  // settings are locked from then on
  streamProvisioned: boolean,
  // 'auto' or <width>x<height>; 'auto' lets the stream host match the source
  resolution: string,
  // 'auto', '30', or '60'; 'auto' lets the stream host match the source
  fps: string,
  start: any,
  stop: any,
  errorList: { field: string, message: string }[],
  channelList: any[],
  channelSelected: any,
  sports: ILookupOption[],
  teams: ILookupOption[],
  securityProfiles: GroupOptions,
  securityProfilesSelected: string[],
  sportSelected: string,
  homeTeamSelected: string,
  awayTeamSelected: string,
  selectedAsset: AssetHit,
  deleteConfirmDialogOpen: boolean,
  stopConfirmDialogOpen: boolean,
  terminateConfirmDialogOpen: boolean,
  recoverConfirmDialogOpen: boolean,
  isExchangeLiveEvent: boolean,
  // For Exchange live events
  exchangeSport: string | null,
  exchangeSeason: string | null,
  exchangeLeague: string | null,
  exchangeDate: string | null,
  exchangeHomeTeam: ITeamOption | null,
  exchangeAwayTeam: ITeamOption | null,
  exchangeGameType: string | null,
  exchangeTeamOptions: ITeamOption[],
  exchangeSportOptions: ILookupOption[],
  exchangeGameTypeOptions: ILookupOption[],
  exchangeLeagueOptions: ILookupOption[],
  exchangeSeasonOptions: ILookupOption[],
}

export enum ActionType {
  OpenModal = 'LiveAssetModal.OpenModel',
  CloseModal = 'LiveAssetModal.CloseModal',
  UpdateAsset = 'LiveAssetModal.UpdateAsset',
  ToggleDisableButton = 'LiveAssetModal.ToggleDisableButton',
  SetDeleteConfirmDialogOpen = 'LiveAssetModal.setDeleteConfirmDialogOpen',
  SetStopConfirmDialogOpen = 'LiveAssetModal.setStopConfirmDialogOpen',
  SetTerminateConfirmDialogOpen = 'LiveAssetModal.setTerminateConfirmDialogOpen',
  SetRecoverConfirmDialogOpen = 'LiveAssetModal.setRecoverConfirmDialogOpen',
  UpdateTeamOptions = 'LiveAssetModal.UpdateTeamOptions',
}

export interface IliveAssetModal extends Action<ActionType> {
  payload?: any
}

const INITIAL_STATE: IState = {
  // searchResults: null,
  isOpened: false,
  extendMode: false,
  restartMode: false,
  isButtonDisabled: false,
  isRTMPFeed: true,
  title: null,
  url: null,
  bitrate: 4000,
  bitrateAuto: true,
  streamProvisioned: false,
  resolution: 'auto',
  fps: 'auto',
  start: null,
  stop: null,
  errorList: [],
  channelList: [],
  channelSelected: null,
  sports: [],
  teams: [],
  securityProfiles: [],
  securityProfilesSelected: [],
  sportSelected: null,
  homeTeamSelected: null,
  awayTeamSelected: null,
  selectedAsset: null,
  deleteConfirmDialogOpen: false,
  stopConfirmDialogOpen: false,
  terminateConfirmDialogOpen: false,
  recoverConfirmDialogOpen: false,
  isExchangeLiveEvent: false,
  // For Exchange live events
  exchangeSport: null,
  exchangeSeason: null,
  exchangeLeague: null,
  exchangeDate: null,
  exchangeHomeTeam: null,
  exchangeAwayTeam: null,
  exchangeGameType: null,
  exchangeSportOptions: [],
  exchangeGameTypeOptions: [],
  exchangeLeagueOptions: [],
  exchangeSeasonOptions: [],
  exchangeTeamOptions: [],
};

export default function reducer(state: IState = INITIAL_STATE, action: IliveAssetModal): IState {
  switch (action.type) {
    case ActionType.OpenModal:
      return {
        ...state,
        isOpened: true,
        errorList: [],
        ...action.payload,
      };
    case ActionType.CloseModal:
      return {
        ...INITIAL_STATE,
      };
    case ActionType.UpdateAsset:
      return {
        ...state,
        ...action.payload,
      };
    case ActionType.ToggleDisableButton:
      return {
        ...state,
        isButtonDisabled: !state.isButtonDisabled,
      };
    case ActionType.SetDeleteConfirmDialogOpen:
      return {
        ...state,
        deleteConfirmDialogOpen: action.payload.open,
        selectedAsset: action.payload.selectedAsset,
      };
    case ActionType.SetStopConfirmDialogOpen:
      return {
        ...state,
        stopConfirmDialogOpen: action.payload.open,
        selectedAsset: action.payload.selectedAsset,
      };
    case ActionType.SetTerminateConfirmDialogOpen:
      return {
        ...state,
        terminateConfirmDialogOpen: action.payload.open,
        selectedAsset: action.payload.selectedAsset,
      };
    case ActionType.SetRecoverConfirmDialogOpen:
      return {
        ...state,
        recoverConfirmDialogOpen: action.payload.open,
        selectedAsset: action.payload.selectedAsset,
      };
    case ActionType.UpdateTeamOptions:
      return {
        ...state,
        exchangeTeamOptions: action.payload.teamOptions,
        exchangeHomeTeam: null,
        exchangeAwayTeam: null,
      };
    default:
      return state;
  }
}

export const openAssetModal = (extendMode: boolean, restartMode: boolean, selectedAsset?: AssetHit, updates?: ILiveAssetUpdate[]) => async (dispatch: Dispatch<IliveAssetModal>, getState: any) => {
  const state: IAppState = getState();

  const userRole = selectUserRole(state);
  const organizationId = userRole[KeyRoleUserVaultRoles][0].orgId;

  const channelList = await getChannelsByOrgId(organizationId).catch((err) => {
    console.log(err);
  });

  // Get options
  const securityProfileOptions = await getSecurityProfileOptions(state);

  // selectOptionsByName
  const sports = selectOptionsByName(LookupName.Sport, state);
  const teams = selectOptionsByName(LookupName.Team, state);

  // Set up exchange values
  const exchangeSportOptions = selectExchangeSportOptions(state, true);
  const exchangeSport = exchangeSportOptions.find((s) => s.value === Sport.MensHockey)?.value ?? '';
  const exchangeGameTypeOptions = selectExchangeGameTypeOptions(state, true);
  const exchangeGameType = exchangeGameTypeOptions.find((gt) => gt.value === 'Regular Season')?.value ?? '';
  const exchangeLeagueOptions = selectLeagueOptions(state, true);
  const exchangeLeague = exchangeLeagueOptions.find((l) => l.value === League.NCAA)?.value ?? '';
  // Seasons
  const date = new Date();
  const currentYear = date.getFullYear();
  const seasons = [...Array(4)].map((_, i) => (currentYear + i).toString());
  seasons.unshift((currentYear - 1).toString());
  const exchangeSeasonOptions = seasons.map((season) => ({ label: season, value: season }));
  const exchangeSeason = exchangeSeasonOptions[1].value; // Current year
  // Teams
  const exchangeTeamOptions = await getTeamsFromApi(exchangeSport, exchangeLeague);

  const payload: any = {
    channelList: channelList ? channelList.data.data : [],
    sports,
    teams,
    securityProfiles: securityProfileOptions,
    extendMode,
    restartMode,
    selectedAsset,
    exchangeSportOptions,
    exchangeSport,
    exchangeGameTypeOptions,
    exchangeGameType,
    exchangeLeagueOptions,
    exchangeLeague,
    exchangeSeasonOptions,
    exchangeSeason,
    exchangeTeamOptions,
  };

  if (selectedAsset) {
    const asset = (await getAsset(selectedAsset?._id))?.data?.data;
    payload.start = asset.start;
    payload.stop = asset.stop;
    payload.title = asset.title;
    payload.url = asset.streamUrl;
    payload.securityProfilesSelected = asset.securityProfiles;
    payload.sportSelected = asset.sport;
    payload.homeTeamSelected = asset.homeTeam;
    payload.awayTeamSelected = asset.awayTeam;
    payload.bitrate = asset.streamBitrate;
    payload.bitrateAuto = !asset.streamBitrate;
    payload.resolution = asset.streamResolution || 'auto';
    payload.fps = asset.streamFps ? String(asset.streamFps) : 'auto';
    payload.streamProvisioned = !!asset.ec2Instance;
    payload.isRTMPFeed = true;
    if (!asset?._source?.streamChannel && asset?.streamUrl) payload.isRTMPFeed = false;
    if (asset.exchangeLiveStreamScheduleId) payload.isExchangeLiveEvent = true;
  }

  if (updates) updates.forEach((val) => { payload[val.name] = val.value; });

  dispatch({
    type: ActionType.OpenModal,
    payload,
  });
};

export const closeNewAssetModal = () => ({
  type: ActionType.CloseModal,
});

export interface ILiveAssetUpdate { name: string, value: any }
export const updateLiveAssetModalProperties = (payload: ILiveAssetUpdate[]) => (dispatch: Dispatch<IliveAssetModal>, getState: () => IAppState) => {
  const { liveAssetModal: updates } = getState() as any;
  payload.forEach((val) => { updates[val.name] = val.value; });

  if (payload.find((p) => p.name === 'exchangeSport' || p.name === 'exchangeLeague')) {
    loadTeams()(dispatch, updates.exchangeSport, updates.exchangeLeague);
  }

  dispatch({
    type: ActionType.UpdateAsset,
    updates,
  });
};

const pad2 = (value: number): string => (value < 10 ? '0' : '') + value;
export const createLiveAsset = (startInit = false, callback?: () => any) => async (dispatch: Dispatch<IliveAssetModal>, getState: () => IAppState) => {
  const state = getState();
  const userRole = selectUserRole(state);
  const {
    title, url, start, stop, channelSelected, securityProfilesSelected, sportSelected, homeTeamSelected, awayTeamSelected, bitrate, bitrateAuto, resolution, fps,
    isExchangeLiveEvent,
    exchangeSport, exchangeSeason, exchangeLeague, exchangeDate, exchangeHomeTeam, exchangeAwayTeam, exchangeGameType,
  } = state.liveAssetModal;

  let startTime: moment.Moment = start;
  if (startInit) startTime = moment();

  const dateValue = new Date(start);
  const gameDate = formUtcStringFromDate(dateValue);
  const userVaultRoles = userRole?.[KeyRoleUserVaultRoles];
  const organizationId = userVaultRoles?.[0]?.orgId ?? -1;

  const securityProfilesMapping = state.securityProfiles?.idValueMapping ?? [];
  const securityProfile = securityProfilesMapping.find(
    (sp: { idm_org_id: number }) => sp.idm_org_id === organizationId,
  );
  let securityProfileId = null;

  if (securityProfile) {
    const { _id } = securityProfile;
    securityProfileId = _id;
  }

  if (!securityProfilesSelected.includes(securityProfileId)) securityProfilesSelected.push(securityProfileId);

  dispatch({
    type: ActionType.ToggleDisableButton,
  });

  let scheduleId: string = null;
  if (isExchangeLiveEvent) {
    const date = new Date(moment(start).startOf('day').utc().toISOString());
    const month = pad2(date.getMonth() + 1);
    const day = pad2(date.getDate());
    const year = date.getFullYear();
    // construct game date with leading -'s
    const gameDate = `${month}/${day}/${year}`;
    const schedules = (await getSchedules(false, gameDate, exchangeSport, exchangeLeague, exchangeSeason)).data.data;
    schedules.forEach((schedule) => {
      if (schedule.HomeTeamId === exchangeHomeTeam.value && schedule.VisitorTeamId === exchangeAwayTeam.value) {
        // Found existing schedule
        toast.info('An existing schedule for this game was found. The existing schedule will be used.', {
          position: toast.POSITION.TOP_RIGHT,
        });
        scheduleId = schedule._id;
      }
    });

    // Handle exchange live event creation
    if (!scheduleId) {
      const res = (await createGame({
        Season: exchangeSeason,
        HomeTeamId: exchangeHomeTeam.value,
        VisitorTeamId: exchangeAwayTeam.value,
        League: exchangeLeague,
        Sport: exchangeSport,
        GameType: exchangeGameType,
        GameDate: gameDate,
      })).data.data;
      scheduleId = res._id;
    }
  }

  await saveLiveAsset({
    start: startTime.toISOString(),
    stop: stop.toISOString(),
    gameDate,
    streamUrl: channelSelected ? channelSelected.url : url,
    title,
    securityProfiles: securityProfilesSelected,
    owner: securityProfileId,
    streamChannel: channelSelected ? channelSelected.name : null,
    channelId: channelSelected ? channelSelected._id : null,
    sport: sportSelected,
    homeTeam: homeTeamSelected,
    awayTeam: awayTeamSelected,
    ...(!bitrateAuto && bitrate && { streamBitrate: Number(bitrate) }),
    ...(resolution && resolution !== 'auto' && { streamResolution: resolution }),
    ...(fps && fps !== 'auto' && { streamFps: Number(fps) }),
    ...(isExchangeLiveEvent && { exchangeLiveStreamScheduleId: scheduleId }),
  });
  if (callback) setTimeout(() => { callback(); }, 1500);

  dispatch({
    type: ActionType.CloseModal,
  });
};

export const restartLiveStream = (id: string, callback?: () => any) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  await restartLiveStreamApi(id);
  if (callback) setTimeout(() => { callback(); }, 1500);
};

export const updateAsset = (id: string, update: any, callback?: () => any) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const tempUpdate = update;
  if (tempUpdate.start) tempUpdate.gameDate = formUtcStringFromDate(new Date(tempUpdate.start));
  const userRole = selectUserRole(getState());
  // Add user info
  tempUpdate.modifiedBy = userRole.userInfo.displayName;
  tempUpdate.modifiedDate = new Date().toISOString();

  await updateAssetApi(id, tempUpdate);
  if (callback) setTimeout(() => { callback(); }, 1500);
};

export const setDeleteConfirmDialogOpen = (open: boolean, selectedAsset: AssetHit) => (dispatch: Dispatch<IliveAssetModal>) => {
  dispatch({
    type: ActionType.SetDeleteConfirmDialogOpen,
    payload: {
      open,
      selectedAsset,
    },
  });
};

export const setStopConfirmDialogOpen = (open: boolean, selectedAsset: AssetHit) => (dispatch: Dispatch<IliveAssetModal>) => {
  dispatch({
    type: ActionType.SetStopConfirmDialogOpen,
    payload: {
      open,
      selectedAsset,
    },
  });
};

export const setTerminateConfirmDialogOpen = (open: boolean, selectedAsset: AssetHit) => (dispatch: Dispatch<IliveAssetModal>) => {
  dispatch({
    type: ActionType.SetTerminateConfirmDialogOpen,
    payload: {
      open,
      selectedAsset,
    },
  });
};

export const setRecoverConfirmDialogOpen = (open: boolean, selectedAsset: AssetHit) => (dispatch: Dispatch<IliveAssetModal>) => {
  dispatch({
    type: ActionType.SetRecoverConfirmDialogOpen,
    payload: {
      open,
      selectedAsset,
    },
  });
};

/**
 * Kicks off recovery of a stranded stream's video.
 *
 * The API answers as soon as the work is accepted - rebuilding the MP4 can take
 * minutes, and longer when a host has to be launched first - so the toast tells
 * the user to watch the status rather than implying it is already done.
 */
export const recoverLiveStream = (id: string, callback?: () => any) => async (
  dispatch: Dispatch,
) => {
  try {
    await recoverLiveStreamApi(id);
    toast('Recovering video from the recorded stream. This can take several minutes - the status will move to Processing, then Complete.', {
      type: toast.TYPE.INFO,
    });
  } catch (error) {
    // Only synchronous rejections land here - wrong stream status, read-only
    // asset, insufficient role. Whether any footage survives in S3 is not known
    // until after the API has answered, so that failure shows up as the status
    // returning to Error rather than as an error here.
    toast('Could not start recovery for this event.', {
      type: toast.TYPE.ERROR,
    });
  }
  if (callback) setTimeout(() => { callback(); }, 1500);
};

const getTeamsFromApi = async (sport: string, league: string) => {
  const teamsRes = await getAllTeamsBySport(sport, league);
  const sorted = (teamsRes.data.data).sort((a, b) => ((a.TeamName > b.TeamName) ? 1 : -1));
  const teamOptions = sorted.map((team) => ({ label: team.TeamName, value: team._id, teamData: team })) as ITeamOption[];
  return teamOptions;
};

export const loadTeams = () => async (dispatch: Dispatch<IliveAssetModal>, exchangeSport: string, exchangeLeague: string) => {
  const teamOptions = await getTeamsFromApi(exchangeSport, exchangeLeague);
  dispatch({
    type: ActionType.UpdateTeamOptions,
    payload: { teamOptions },
  });
};
